// Query params the plan studio puts on /generate-ads. Captured once at
// mount: productIds is stripped from the URL after it is absorbed, and
// the cap has to survive that cleanup.

import { isSecretText } from '../../marketingPlans/client';
import { readAdKinds, type PlanAdKinds } from '../../marketingPlans/detail';

export type GenerateHandoff = {
  capUsd: number | null;
  kinds: PlanAdKinds | null;
  planId: string | null;
  planLineKey: string | null;
  revision: string | null;
  productIds: string[];
  creativeType: string | null;
  generateCount: number | null;
  lineCampaignRequired: boolean;
};

function readToken(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 80 || /\s/.test(trimmed) || isSecretText(trimmed)) return null;
  return trimmed;
}

export function readCapUsd(value: string | null): number | null {
  if (!value || !value.trim()) return null;
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100) / 100;
}

function readCreativeType(value: string | null): string | null {
  const token = readToken(value);
  if (!token) return null;
  const slug = token.toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return null;
  return slug;
}

function readGenerateCount(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value.trim())) return null;
  const count = Number(value);
  if (!Number.isInteger(count) || count <= 0) return null;
  return count;
}

export function readGenerateHandoff(params: URLSearchParams): GenerateHandoff {
  const productIds = (params.get('productIds') || '')
    .split(',')
    .map((item) => readToken(item))
    .filter((item): item is string => Boolean(item));
  return {
    capUsd: readCapUsd(params.get('capUsd')),
    kinds: readAdKinds(params.get('kinds')),
    planId: readToken(params.get('planId')),
    planLineKey: readToken(params.get('planLineKey')),
    revision: readToken(params.get('revision')),
    productIds,
    creativeType: readCreativeType(params.get('creativeType')),
    generateCount: readGenerateCount(params.get('generateCount')),
    lineCampaignRequired: params.get('lineCampaign') === 'required'
  };
}
